/**
 * TokenValueBadge Component
 *
 * Displays estimated SOL value on token cards for tokens eligible for dust swapping.
 * Shows loading state when prices are being fetched.
 * Shows faded value with tooltip for tokens below minimum threshold.
 */

import { DUST_SWAP_CONSTANTS } from '@/lib/constants';

interface TokenValueBadgeProps {
  /** Estimated SOL value from Jupiter quote */
  estimatedSolValue: number;
  /** Whether the token meets minimum swap threshold (has route AND meets minimum) */
  isSwappable: boolean;
  /** Whether Jupiter found a swap route for this token */
  hasSwapRoute?: boolean;
  /** Whether the token meets the minimum value threshold */
  meetsMinimum?: boolean;
  /** Whether prices are being fetched */
  isLoading?: boolean;
  /** Compact mode for smaller displays */
  compact?: boolean;
}

/**
 * Format SOL value for display as Credits
 */
function formatSolValue(value: number, compact: boolean = false): string {
  if (value === 0) return compact ? '0 Credits' : '0 Credits';
  if (value < 0.0001) return compact ? '<0.0001 Credits' : '<0.0001 Credits';
  if (value < 0.001) return compact ? `${value.toFixed(4)} Credits` : `${value.toFixed(4)} Credits`;
  if (value < 0.01) return compact ? `${value.toFixed(3)} Credits` : `${value.toFixed(3)} Credits`;
  return compact ? `${value.toFixed(2)} Credits` : `${value.toFixed(2)} Credits`;
}

export function TokenValueBadge({
  estimatedSolValue,
  isSwappable,
  hasSwapRoute = false,
  meetsMinimum = false,
  isLoading = false,
  compact = false,
}: TokenValueBadgeProps) {
  // Show loading state
  if (isLoading) {
    return (
      <div
        className="mt-1 flex items-center justify-center gap-1"
        style={{ fontFamily: 'Outfit, sans-serif' }}
      >
        <span
          className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-t-transparent"
          style={{ borderColor: '#4ADE80', borderTopColor: 'transparent' }}
        />
        {!compact && (
          <span className="text-xs" style={{ color: '#9CA3AF' }}>
            ...
          </span>
        )}
      </div>
    );
  }

  // Don't render if no swap route found
  if (!hasSwapRoute && !isSwappable) {
    return null;
  }

  // Token has route but below minimum - show faded value with tooltip
  if (hasSwapRoute && !meetsMinimum) {
    const minValue = DUST_SWAP_CONSTANTS.MIN_SWAP_VALUE_SOL;
    return (
      <div
        className={`mt-1 text-center font-semibold ${compact ? 'text-xs' : 'text-xs'}`}
        style={{
          fontFamily: 'Outfit, sans-serif',
          color: '#9CA3AF', // Gray/faded color
          opacity: 0.7,
        }}
        title={`Value: ${estimatedSolValue.toFixed(6)} Credits. Minimum ${minValue} Credits required to swap.`}
      >
        {formatSolValue(estimatedSolValue, compact)}
      </div>
    );
  }

  // Swappable token - show green value
  return (
    <div
      className={`mt-1 text-center font-semibold ${compact ? 'text-xs' : 'text-xs'}`}
      style={{
        fontFamily: 'Outfit, sans-serif',
        color: '#4ADE80',
      }}
      title={`Estimated swap value: ${estimatedSolValue.toFixed(6)} Credits`}
    >
      {formatSolValue(estimatedSolValue, compact)}
    </div>
  );
}
